import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CompleteOnboardingNewOrgInput,
  completeOnboardingNewOrgSchema,
  type TeamSize,
  teamSizeOptions,
} from '@sea-snag/shared';
import { Badge, Button, Input, Label } from '@sea-snag/ui';
import { HTTPError } from 'ky';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuth } from '../../hooks/use-auth';
import { setOrganization } from '../../stores/org-store';

const teamSizeLabels: Record<TeamSize, string> = {
  'just-me': 'Just me',
  '2-5': '2–5',
  '6-20': '6–20',
  '21-50': '21–50',
  '51+': '51+',
};

interface StepOrgInfoProps {
  email: string;
  token: string;
  onComplete: (orgPid: string) => void;
}

export function StepOrgInfo({ email, token, onComplete }: StepOrgInfoProps) {
  const { completeOnboardingNewOrg } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CompleteOnboardingNewOrgInput>({
    resolver: zodResolver(completeOnboardingNewOrgSchema),
    defaultValues: {
      token,
    },
  });

  const selectedTeamSize = watch('teamSize');

  const onSubmit = async (data: CompleteOnboardingNewOrgInput) => {
    setError(null);
    try {
      const result = await completeOnboardingNewOrg(data);
      setOrganization({
        pid: result.organization.id,
        name: result.organization.name,
        slug: result.organization.slug,
        setupCompletedAt: null,
      });
      onComplete(result.organization.id);
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Something went wrong');
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5"
      aria-label="Organization setup form"
    >
      {error && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Email display */}
      <div className="space-y-2">
        <Label>Email</Label>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm">
            {email}
          </Badge>
        </div>
      </div>

      {/* Organization name */}
      <div className="space-y-2">
        <Label htmlFor="organizationName">New organization name</Label>
        <Input
          id="organizationName"
          type="text"
          placeholder="Acme Charter"
          autoFocus
          aria-describedby={errors.organizationName ? 'org-error' : undefined}
          {...register('organizationName')}
        />
        {errors.organizationName && (
          <p id="org-error" className="text-sm text-destructive">
            {errors.organizationName.message}
          </p>
        )}
      </div>

      {/* Team size */}
      <div className="space-y-2">
        <Label>Team size</Label>
        <div className="grid grid-cols-5 gap-2">
          {teamSizeOptions.map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => setValue('teamSize', size, { shouldValidate: true })}
              className={`rounded-lg border-2 px-2 py-2.5 text-center text-sm font-medium transition-all ${
                selectedTeamSize === size
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {teamSizeLabels[size]}
            </button>
          ))}
        </div>
        {errors.teamSize && <p className="text-sm text-destructive">{errors.teamSize.message}</p>}
      </div>

      <input type="hidden" {...register('token')} />

      <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
        {isSubmitting ? 'Creating organization...' : 'Continue'}
      </Button>
    </form>
  );
}
